import React, { useState, useCallback, useEffect, useRef } from 'react';

// ==========================================
// TYPES & INTERFACES
// ==========================================
interface FormData {
  name: string;
  email: string;
  phone: string;
  country: string;
  otherCountry: string;
  companyName: string;
  productInterest: string;
  incoterm: string;
  estimatedVolume: string;
  destinationPort: string;
  message: string;
}

// ==========================================
// INLINE MINI SVG ICONS (Optimized Scale)
// ==========================================
const ShieldCheckIcon = () => (
  <svg className="mini-card-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 11 2 2 4-4" />
  </svg>
);

const BoxIcon = () => (
  <svg className="mini-card-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
    <line x1="12" y1="22.08" x2="12" y2="12" />
  </svg>
);

const ClockIcon = () => (
  <svg className="mini-card-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2.5">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const CertificateIcon = () => (
  <svg className="mini-card-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
    <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const EmailIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);

// ==========================================
// MAIN RECONFIGURED COMPONENT
// ==========================================
const GlobalTrade: React.FC = () => {
  const sectionsRef = useRef<(HTMLDivElement | null)[]>([]);
  const [visibleSections, setVisibleSections] = useState<Set<number>>(new Set());

  // RFQ Global Form State
  const [formData, setFormData] = useState<FormData>({
    name: '', email: '', phone: '', country: '', otherCountry: '',
    companyName: '', productInterest: '', incoterm: '', 
    estimatedVolume: '', destinationPort: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Smooth Viewport Reveals via Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = sectionsRef.current.findIndex((el) => el === entry.target);
          if (entry.isIntersecting && index !== -1) {
            setVisibleSections((prev) => new Set(prev).add(index));
          }
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.08 }
    );

    const currentSections = sectionsRef.current.filter(Boolean);
    currentSections.forEach((section) => observer.observe(section!));

    return () => {
      currentSections.forEach((section) => observer.unobserve(section!));
    };
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const validateForm = useCallback((): boolean => {
    return formData.name.trim() !== '' && 
           formData.email.trim() !== '' && 
           formData.phone.trim() !== '' && 
           formData.country !== '' &&
           formData.productInterest !== '' &&
           formData.incoterm !== '' &&
           formData.message.trim() !== '';
  }, [formData]);

  const handleFormSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validateForm()) {
      setSubmitError('Please complete all mandatory parameters.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('https://formsubmit.co/ajax/wardhamushroom@gmail.com', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          ...formData,
          _subject: `B2B Global Trade Request - ${formData.companyName || formData.name}`,
          _captcha: 'false'
        })
      });

      if (!response.ok) throw new Error('Transmission fault.');
      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError('Transmission fault. Please direct your requirements directly to wardhamushroom@gmail.com.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm]);

  return (
    <div className="gt-module">
      {/* SCOPED INDUSTRIAL CSS FRAMEWORK */}
      <style>{`
        .gt-module {
          width: 100%;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          background: #0b0f19;
          color: #f3f4f6;
          overflow-x: hidden;
        }
        
        /* Elite Hero Banner */
        .gt-hero {
          background: linear-gradient(rgba(11, 15, 25, 0.88), rgba(7, 10, 17, 0.98)), url('/images/globaltradenew.webp') center/cover no-repeat;
          padding: 120px 20px;
          text-align: center;
          border-bottom: 1px solid #1e293b;
        }
        .gt-hero-content {
          max-width: 950px;
          margin: 0 auto;
        }
        .gt-hero h1 {
          font-size: 3.2rem;
          font-weight: 900;
          color: #ffffff;
          letter-spacing: -1px;
          margin: 0 0 20px 0;
          text-transform: uppercase;
        }
        .gt-hero p {
          font-size: 1.25rem;
          color: #9ca3af;
          line-height: 1.65;
          margin: 0;
          font-weight: 300;
        }

        /* Reveal Transitions */
        .gt-section {
          opacity: 0;
          transform: translateY(25px);
          transition: opacity 0.7s cubic-bezier(0.16, 1, 0.3, 1), transform 0.7s cubic-bezier(0.16, 1, 0.3, 1);
          width: 100%;
        }
        .gt-section.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* Component Headers */
        .gt-section-header {
          text-align: center;
          margin-bottom: 50px;
        }
        .gt-section-header h2 {
          font-size: 2.2rem;
          font-weight: 900;
          color: #ffffff;
          text-transform: uppercase;
          margin: 0 0 10px 0;
          letter-spacing: 0.5px;
        }
        .gt-section-header .accent-line {
          width: 50px;
          height: 4px;
          background: #f97316;
          margin: 0 auto 18px auto;
        }
        .gt-section-header p {
          font-size: 1.15rem;
          color: #9ca3af;
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* 1. WeExport Grid Section */
        .we-export {
          padding: 90px 20px;
          background: #0b0f19;
        }
        .export-details {
          max-width: 1200px;
          margin: 0 auto;
        }
        .products-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 30px;
        }
        .product {
          background: #111827;
          border: 1px solid #1e293b;
          border-radius: 8px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          transition: all 0.4s ease;
        }
        .product:hover {
          transform: translateY(-6px);
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
          border-color: #f97316;
        }
        .product-image {
          width: 100%;
          height: 240px;
          object-fit: cover;
          background: #1f2937;
        }
        .product-info {
          padding: 25px;
          text-align: left;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }
        .product h4 {
          font-size: 1.3rem;
          font-weight: 800;
          color: #ffffff;
          margin: 0 0 12px 0;
          border-left: 3px solid #f97316;
          padding-left: 10px;
        }
        .product p {
          font-size: 0.95rem;
          color: #9ca3af;
          line-height: 1.55;
          margin: 0 0 20px 0;
        }
        .product-specs {
          margin-top: auto;
          padding-top: 15px;
          border-top: 1px solid #1e293b;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .spec-badge {
          background: rgba(249, 115, 22, 0.1);
          color: #fdba74;
          font-size: 0.72rem;
          font-weight: 700;
          padding: 5px 10px;
          border-radius: 4px;
          text-transform: uppercase;
          border: 1px solid rgba(249, 115, 22, 0.15);
        }

        /* 2. RECONFIGURED SINGLE-ROW LOGISTICS ROW */
        .trade-assurance {
          padding: 80px 20px;
          background: #0f172a;
          border-y: 1px solid #1e293b;
        }
        .assurance-row-container {
          max-width: 1200px;
          margin: 0 auto;
        }
        .assurance-row {
          display: grid;
          grid-template-columns: repeat(4, 1fr); /* Rigid 4-column single row assignment */
          gap: 16px;
        }
        .assurance-mini-card {
          background: #1e293b;
          border: 1px solid #334155;
          border-radius: 6px;
          padding: 20px 16px;
          text-align: left;
          position: relative;
          transition: all 0.25s ease;
        }
        .assurance-mini-card:hover {
          border-color: #3b82f6;
          background: #243249;
        }
        .assurance-mini-card.security-accent:hover {
          border-color: #10b981;
        }
        .card-header-inline {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
        }
        .mini-card-icon {
          flex-shrink: 0;
        }
        .assurance-mini-card h3 {
          font-size: 0.95rem;
          font-weight: 800;
          margin: 0;
          color: #ffffff;
          text-transform: uppercase;
          letter-spacing: 0.3px;
          line-height: 1.2;
        }
        .assurance-mini-card p {
          font-size: 0.82rem;
          color: #94a3b8;
          line-height: 1.45;
          margin: 0;
        }

        /* 3. B2B RFQ Secure Portal */
        .import-enquiry {
          padding: 90px 20px;
          background: #0b0f19;
          display: flex;
          justify-content: center;
        }
        .enquiry-container {
          background: #ffffff;
          padding: 50px 45px;
          border-radius: 10px;
          width: 100%;
          max-width: 740px;
          color: #0f172a;
          box-shadow: 0 30px 60px rgba(0, 0, 0, 0.6);
        }
        .enquiry-container h2 {
          font-size: 2.2rem;
          font-weight: 900;
          margin: 0 0 8px 0;
          color: #0f172a;
          text-transform: uppercase;
          letter-spacing: -0.5px;
        }
        .enquiry-subtitle {
          font-size: 1.05rem;
          color: #475569;
          margin: 0 0 35px 0;
          line-height: 1.5;
        }
        .enquiry-form {
          display: flex;
          flex-direction: column;
          gap: 22px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 22px;
        }
        .input-group {
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .input-group label {
          font-size: 0.8rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .input-group input,
        .input-group select,
        .input-group textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid #e2e8f0;
          border-radius: 5px;
          font-size: 0.95rem;
          color: #0f172a;
          background-color: #f8fafc;
          transition: all 0.2s ease;
          box-sizing: border-box;
          font-weight: 500;
        }
        .input-group input:focus,
        .input-group select:focus,
        .input-group textarea:focus {
          border-color: #0f172a;
          background-color: #ffffff;
          outline: none;
          box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.06);
        }
        .input-group svg {
          position: absolute;
          right: 16px;
          bottom: 13px;
          color: #94a3b8;
          pointer-events: none;
        }
        .enquiry-button {
          padding: 16px;
          font-size: 1.05rem;
          font-weight: 700;
          background-color: #0f172a;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-top: 10px;
        }
        .enquiry-button:hover:not(:disabled) {
          background-color: #1e293b;
          transform: translateY(-1px);
        }
        .enquiry-button:disabled {
          background-color: #cbd5e1;
          cursor: not-allowed;
        }
        .error-message {
          background: #fef2f2;
          border: 2px solid #fee2e2;
          color: #991b1b;
          padding: 12px;
          border-radius: 5px;
          font-size: 0.9rem;
          font-weight: 600;
        }
        .corporate-footer {
          margin-top: 35px;
          padding-top: 25px;
          border-top: 2px solid #f1f5f9;
          display: flex;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 15px;
          font-size: 0.85rem;
          color: #64748b;
          font-weight: 600;
        }

        /* Responsive Flow Overrides */
        @media (max-width: 1024px) {
          .assurance-row {
            grid-template-columns: 1fr 1fr; /* 2x2 split for standard tablets */
            gap: 14px;
          }
        }
        @media (max-width: 768px) {
          .gt-hero h1 { font-size: 2.2rem; }
          .gt-section-header h2 { font-size: 1.8rem; }
          .form-row { grid-template-columns: 1fr; }
          .enquiry-container { padding: 30px 20px; }
          .assurance-row { grid-template-columns: 1fr; } /* Vertical stack on micro phone displays */
        }
      `}</style>

      {/* HERO SECTION WITH ENTERPRISE TITLE */}
      <div className="gt-hero">
        <div className="gt-hero-content">
          <h1>Global Agro-Logistics &amp; Industrial Mushroom Export Hub</h1>
          <p>
            Securing international commercial supply loops via strict cross-border compliance, automated cold-chain configurations, and comprehensive institutional risk assurance matrices.
          </p>
        </div>
      </div>

      {/* COMPONENT 1: PRODUCT MATRIX */}
      <div 
        ref={(el) => { sectionsRef.current[1] = el; }} 
        className={`gt-section ${visibleSections.has(1) ? 'fade-in' : ''}`}
      >
        <section className="we-export">
          <div className="export-details">
            <div className="gt-section-header">
              <h2>Export Infrastructure Portfolio</h2>
              <div className="accent-line"></div>
              <p>Engineered to withstand complex multi-modal maritime and air freight corridors while locking down biological tissue integrity.</p>
            </div>
            
            <div className="products-container">
              <div className="product">
                <img src="/images/fresh.webp" alt="White Button Export Cargo" className="product-image" />
                <div className="product-info">
                  <h4>Premium White Button Class</h4>
                  <p>Vacuum-cooled immediately post-harvest to drop core heat signatures. Packaged under high-barrier macro-atmosphere profiles to halt structural degradation during transport.</p>
                  <div className="product-specs">
                    <span className="spec-badge">Pre-Cooled Chain</span>
                    <span className="spec-badge">Caliber: 35-50mm</span>
                  </div>
                </div>
              </div>

              <div className="product">
                <img src="/images/dried.webp" alt="Dehydrated Gourmet Matrix" className="product-image" />
                <div className="product-info">
                  <h4>Dehydrated Gourmet Oyster Matrix</h4>
                  <p>Moisture-calibrated under uniform industrial dehydration loops. Designed for total rehydration capability without cell-wall collapse for commercial production lines.</p>
                  <div className="product-specs">
                    <span className="spec-badge">Moisture &lt; 12%</span>
                    <span className="spec-badge">Multi-Wall Foil Bags</span>
                  </div>
                </div>
              </div>

              <div className="product">
                <img src="/images/powder.webp" alt="Mushroom Powder Drums" className="product-image" />
                <div className="product-info">
                  <h4>Pure Industrial Mushroom Powder</h4>
                  <p>Milled from targeted dehydrated caps into a perfectly uniform mesh asset. Free from fillers, preserving intense functional umami characteristics for food processing systems.</p>
                  <div className="product-specs">
                    <span className="spec-badge">100% Mesh Grading</span>
                    <span className="spec-badge">Sealed Nitrogen Drums</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* COMPONENT 2: STREAMLINED LOGISTICS ROW (ALL IN 1 ROW ON DESKTOP) */}
      <div 
        ref={(el) => { sectionsRef.current[2] = el; }} 
        className={`gt-section ${visibleSections.has(2) ? 'fade-in' : ''}`}
      >
        <section className="trade-assurance">
          <div className="assurance-row-container">
            <div className="gt-section-header">
              <h2>Global Trade Security &amp; Logistics Protocol</h2>
              <div className="accent-line" style={{ background: '#3b82f6' }}></div>
            </div>

            <div className="assurance-row">
              {/* Card 1 */}
              <div className="assurance-mini-card security-accent">
                <div className="card-header-inline">
                  <ShieldCheckIcon />
                  <h3>Contract Security</h3>
                </div>
                <p>Structured around secure Irrevocable Letters of Credit (L/C) and international Escrow mechanics.</p>
              </div>

              {/* Card 2 */}
              <div className="assurance-mini-card">
                <div className="card-header-inline">
                  <BoxIcon />
                  <h3>Barrier Packaging</h3>
                </div>
                <p>Equipped with multi-layer barrier shields and custom anti-humidity desiccant complexes.</p>
              </div>

              {/* Card 3 */}
              <div className="assurance-mini-card">
                <div className="card-header-inline">
                  <ClockIcon />
                  <h3>Transit Performance</h3>
                </div>
                <p>Pre-marching customs paperwork and optimizing maritime corridors to eliminate port lag.</p>
              </div>

              {/* Card 4 */}
              <div className="assurance-mini-card security-accent">
                <div className="card-header-inline">
                  <CertificateIcon />
                  <h3>Phytosanitary Trace</h3>
                </div>
                <p>Verified Phytosanitary Certificates and real-time cold-chain data loggers attached to cargo lots.</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* COMPONENT 3: SECURE B2B RFQ PORTAL */}
      <div 
        ref={(el) => { sectionsRef.current[3] = el; }} 
        className={`gt-section ${visibleSections.has(3) ? 'fade-in' : ''}`}
      >
        <section className="import-enquiry">
          <div className="enquiry-container">
            {submitSuccess ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <h2 style={{ color: '#10b981' }}>Trade Parameters Authenticated</h2>
                <p className="enquiry-subtitle" style={{ marginBottom: 0, fontWeight: 600 }}>
                  Data packet successfully routed to our global trade desk at wardhamushroom@gmail.com. A commercial asset coordinator will issue verified spot pricing and logistics matrices within 24 business hours.
                </p>
              </div>
            ) : (
              <>
                <h2>Institutional Trade RFQ Desk</h2>
                <p className="enquiry-subtitle">Transmit your commercial procurement parameters directly into our system. All data fields are routed directly to our trade logistics infrastructure.</p>

                {submitError && <div className="error-message">{submitError}</div>}

                <form onSubmit={handleFormSubmit} className="enquiry-form">
                  <div className="form-row">
                    <div className="input-group">
                      <label>Authorized Officer Name*</label>
                      <input type="text" name="name" placeholder="e.g., John Doe" value={formData.name} onChange={handleInputChange} required />
                    </div>
                    <div className="input-group">
                      <label>Registered Enterprise Entity</label>
                      <input type="text" name="companyName" placeholder="e.g., Global Foods Sourcing Ltd." value={formData.companyName} onChange={handleInputChange} />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="input-group">
                      <label>Corporate Communications Email*</label>
                      <input type="email" name="email" placeholder="procurement@enterprise.com" value={formData.email} onChange={handleInputChange} required />
                      <EmailIcon />
                    </div>
                    <div className="input-group">
                      <label>Direct Secure Comms Line*</label>
                      <input type="tel" name="phone" placeholder="+1 (555) 000-0000" value={formData.phone} onChange={handleInputChange} required />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="input-group">
                      <label>Target Import Economy*</label>
                      <select name="country" value={formData.country} onChange={handleInputChange} required>
                        <option value="" disabled>Select Target Market</option>
                        <option value="USA">United States</option>
                        <option value="Canada">Canada</option>
                        <option value="Israel">Israel</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Germany">Germany</option>
                        <option value="Netherlands">Netherlands</option>
                        <option value="Japan">Japan</option>
                        <option value="Other">Other Global Location</option>
                      </select>
                    </div>
                    <div className="input-group">
                      <label>Incoterms Selection (2020)*</label>
                      <select name="incoterm" value={formData.incoterm} onChange={handleInputChange} required>
                        <option value="" disabled>Select Shipping Terms</option>
                        <option value="FOB">FOB - Free On Board (JNPT / Mumbai)</option>
                        <option value="CIF">CIF - Cost, Insurance &amp; Freight</option>
                        <option value="CFR">CFR - Cost and Freight</option>
                        <option value="EXW">EXW - Ex Works</option>
                      </select>
                    </div>
                  </div>

                  {formData.country === 'Other' && (
                    <div className="input-group">
                      <label>Specify Trade Jurisdiction*</label>
                      <input type="text" name="otherCountry" placeholder="Identify country or economic zone" value={formData.otherCountry} onChange={handleInputChange} required />
                    </div>
                  )}

                  <div className="form-row">
                    <div className="input-group">
                      <label>Product Stream Component*</label>
                      <select name="productInterest" value={formData.productInterest} onChange={handleInputChange} required>
                        <option value="" disabled>Select Product Class</option>
                        <option value="Fresh White Button">Premium Fresh White Button</option>
                        <option value="Fresh Oyster">Fresh Gourmet Oyster</option>
                        <option value="Dried Oyster">Dehydrated Gourmet Oyster Matrix</option>
                        <option value="Dried Oyster Powder">Pure Industrial Mushroom Powder</option>
                      </select>
                    </div>
                    <div className="input-group">
                      <label>Designated Port of Discharge</label>
                      <input type="text" name="destinationPort" placeholder="e.g., Port of Rotterdam (NLRTM)" value={formData.destinationPort} onChange={handleInputChange} />
                      <GlobeIcon />
                    </div>
                  </div>

                  <div className="input-group">
                    <label>Contract Volume Requirements (Metric Tons / MT Allocation)</label>
                    <input type="text" name="estimatedVolume" placeholder="e.g., 20 MT Spot Allocation" value={formData.estimatedVolume} onChange={handleInputChange} />
                  </div>

                  <div className="input-group">
                    <label>Technical Specifications, Risk Mitigation &amp; Packaging Notes*</label>
                    <textarea name="message" rows={4} placeholder="Specify required target payment tools (e.g., L/C profiles), precise maritime transit restrictions, or target micron density requirements." value={formData.message} onChange={handleInputChange} required />
                  </div>

                  <button type="submit" className="enquiry-button" disabled={isSubmitting}>
                    {isSubmitting ? 'Authenticating Trade Routing Matrix...' : 'Transmit Official Trade RFQ'}
                  </button>
                </form>

                <div className="corporate-footer">
                  <div>💼 Trade Desk Primary: wardhamushroom@gmail.com</div>
                  <div>🌐 Global Registry Interface: +91 7263071957</div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default GlobalTrade;